import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Shield, Zap, Settings, Lock, Eye, Activity, UserCog,
} from 'lucide-react';

import { API_URL, STATUS_COLORS } from '../lib/constants';
import {
  ProgressRing, Badge, Card, StatCard, CATEGORIES,
} from '../components/ui-custom/ComplianceUI';
import SecurityScanningWidget from '../components/widgets/SecurityScanningWidget';
import AccessibilityWidget from '../components/widgets/AccessibilityWidget';

/**
 * The /dashboard landing view.
 *
 * Pulls together: readiness score, controls summary, findings summary,
 * connector health, system version/DB status, compliance roster,
 * user-management summary, and the security/accessibility widgets.
 */
export default function DashboardOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [systemStatus, setSystemStatus] = useState(null);
  const [rosterSummary, setRosterSummary] = useState(null);
  const [userSummary, setUserSummary] = useState(null);
  const [policiesSummary, setPoliciesSummary] = useState(null);

  const categoryIcons = {
    security: Shield,
    availability: Zap,
    processing_integrity: Settings,
    confidentiality: Lock,
    privacy: Eye,
  };

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const [readinessRes, controlsRes, findingsRes, connectorsRes, statusRes, rosterRes, policiesRes] =
          await Promise.all([
            fetch(`${API_URL}/api/audit/readiness-score`, { credentials: 'include' }),
            fetch(`${API_URL}/api/controls/summary`, { credentials: 'include' }),
            fetch(`${API_URL}/api/findings/summary`, { credentials: 'include' }),
            fetch(`${API_URL}/api/connectors/health`, { credentials: 'include' }),
            fetch(`${API_URL}/api/system/status`, { credentials: 'include' }),
            fetch(`${API_URL}/api/roster`, { credentials: 'include' }),
            fetch(`${API_URL}/api/policies/summary`, { credentials: 'include' }),
          ]);

        const readiness = readinessRes.ok ? await readinessRes.json() : null;
        const controlsData = controlsRes.ok ? await controlsRes.json() : null;
        const findings = findingsRes.ok ? await findingsRes.json() : null;
        const connectors = connectorsRes.ok ? await connectorsRes.json() : null;
        const status = statusRes.ok ? await statusRes.json() : null;
        const roster = rosterRes.ok ? await rosterRes.json() : null;
        const policies = policiesRes.ok ? await policiesRes.json() : null;

        // Transform categories array → by_category object
        let controls = controlsData;
        if (controlsData?.categories && Array.isArray(controlsData.categories)) {
          controls = {
            ...controlsData,
            by_category: controlsData.categories.reduce((acc, cat) => {
              acc[cat.category] = cat;
              return acc;
            }, {}),
            compliance_pct: controlsData.overall_compliance_pct || 0,
            total: controlsData.total_controls || 51,
          };
        }

        setData({ readiness, controls, findings, connectors });
        setSystemStatus(status);
        if (policies && typeof policies.active === 'number') {
          setPoliciesSummary(policies);
        }
        if (roster?.roles) {
          const total = roster.roles.length;
          const assigned = roster.roles.filter((r) => r.person_name && r.person_email).length;
          setRosterSummary({ total, assigned, unassigned: total - assigned });
        }

        // User-management summary — best-effort (will 401/403 for plain Users)
        try {
          const uRes = await fetch(`${API_URL}/api/users`, { credentials: 'include' });
          if (uRes.ok) {
            const u = await uRes.json();
            const list = u.users || [];
            setUserSummary({
              total: list.length,
              active: list.filter((x) => x.is_active).length,
              admins: list.filter((x) => x.role === 'admin').length,
              users: list.filter((x) => x.role === 'user').length,
            });
          }
        } catch {
          /* user doesn't have access — ignore */
        }
      } catch (error) {
        console.error('Dashboard fetch error:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner" />
      </div>
    );
  }

  const readiness = data?.readiness;
  const controls = data?.controls;
  const findings = data?.findings;
  const connectors = data?.connectors;

  const overallPct = controls?.compliance_pct || controls?.overall_compliance_pct || 0;
  const totalControls = controls?.total || controls?.total_controls || 51;
  const openFindings = (findings?.by_status?.open || 0) + (findings?.by_status?.in_progress || 0);

  return (
    <div className="dashboard-page">
      <div className="system-status-bar">
        <div className="status-item">
          <span className="status-label">Version</span>
          <span className="status-value">{systemStatus?.version || '1.0'}</span>
        </div>
        <div className="status-item">
          <span className="status-label">Release Date</span>
          <span className="status-value">{systemStatus?.release_date || 'March 26, 2026'}</span>
        </div>
        <div className="status-item">
          <span className="status-label">Database</span>
          <span className="status-value db-status">
            <span
              className={`status-indicator ${
                systemStatus?.database?.connected ? 'connected' : 'disconnected'
              }`}
            />
            {systemStatus?.database?.connected ? 'Connected' : 'Disconnected'}
            {systemStatus?.database?.connected && (
              <span className="db-name">({systemStatus?.database?.name})</span>
            )}
          </span>
        </div>
      </div>

      <div className="page-header">
        <div>
          <h1>Compliance Dashboard</h1>
          <p>SOC 2 Trust Services Criteria — Real-time Status</p>
        </div>
        <Badge color={readiness?.overall_score >= 70 ? '#059669' : '#d97706'}>
          Readiness: {readiness?.grade || 'N/A'} ({readiness?.overall_score || 0}%)
        </Badge>
      </div>

      <div className="stats-grid" data-testid="metrics-grid">
        <StatCard label="Total Controls" value={totalControls} sub="Across 5 TSC categories" />
        <StatCard
          label="Overall Compliance"
          value={`${overallPct}%`}
          color={overallPct >= 80 ? '#059669' : '#d97706'}
        />
        <StatCard
          label="Open Findings"
          value={openFindings}
          sub={findings?.open_critical ? `${findings.open_critical} Critical` : 'None critical'}
          color={openFindings > 0 ? '#d97706' : '#059669'}
        />
        <StatCard
          label="Active Policies"
          value={policiesSummary ? policiesSummary.active : '…'}
          sub={
            policiesSummary
              ? policiesSummary.active === policiesSummary.total
                ? `All ${policiesSummary.total} aligned to SOC 2`
                : `${policiesSummary.active} active of ${policiesSummary.total} total`
              : 'Loading…'
          }
          color="#2563eb"
        />
      </div>

      <h2 className="section-title">Trust Services Categories</h2>
      <div className="categories-grid">
        {controls?.by_category
          ? Object.entries(controls.by_category).map(([key, cat]) => {
              const meta = CATEGORIES[key] || { label: key, color: '#6b7280' };
              const Icon = categoryIcons[key] || Shield;
              const pct = cat.compliance_pct || 0;
              return (
                <Card key={key} className="category-card">
                  <div className="ring-container">
                    <ProgressRing pct={pct} size={100} stroke={8} color={meta.color} />
                    <div className="ring-value" style={{ color: meta.color }}>
                      {pct}%
                    </div>
                  </div>
                  <div className="category-label">
                    <Icon size={16} style={{ color: meta.color }} />
                    {meta.label}
                  </div>
                  <div className="category-sub">
                    {cat.passing || 0}/{cat.total || 0} passing
                  </div>
                </Card>
              );
            })
          : Object.entries(CATEGORIES).map(([key, meta]) => {
              const Icon = categoryIcons[key] || Shield;
              return (
                <Card key={key} className="category-card">
                  <div className="ring-container">
                    <ProgressRing pct={0} size={100} stroke={8} color={meta.color} />
                    <div className="ring-value" style={{ color: meta.color }}>
                      0%
                    </div>
                  </div>
                  <div className="category-label">
                    <Icon size={16} style={{ color: meta.color }} />
                    {meta.label}
                  </div>
                  <div className="category-sub">0/0 passing</div>
                </Card>
              );
            })}
      </div>

      <Link to="/roster" className="roster-dashboard-card" data-testid="dashboard-roster-card">
        <div className="roster-dashboard-icon">
          <UserCog size={28} />
        </div>
        <div className="roster-dashboard-info">
          <h3>Compliance Roster</h3>
          <p>
            {rosterSummary
              ? `${rosterSummary.assigned} of ${rosterSummary.total} roles assigned${
                  rosterSummary.unassigned > 0
                    ? ` — ${rosterSummary.unassigned} still need an owner`
                    : ''
                }`
              : 'Manage role assignments referenced in policies'}
          </p>
        </div>
        <div className="roster-dashboard-action">
          <span>Open roster</span>
          <span className="arrow">→</span>
        </div>
      </Link>

      {userSummary && (
        <Link
          to="/users"
          className="roster-dashboard-card users-dashboard-card"
          data-testid="dashboard-users-card"
        >
          <div
            className="roster-dashboard-icon"
            style={{ background: 'rgba(139, 92, 246, 0.18)', color: '#a78bfa' }}
          >
            <Lock size={28} />
          </div>
          <div className="roster-dashboard-info">
            <h3>User Management</h3>
            <p>
              {userSummary.active} active user{userSummary.active === 1 ? '' : 's'}
              {userSummary.admins > 0
                ? ` · ${userSummary.admins} admin${userSummary.admins === 1 ? '' : 's'}`
                : ''}
              {userSummary.users > 0
                ? ` · ${userSummary.users} member${userSummary.users === 1 ? '' : 's'}`
                : ''}
              . Grant or revoke access per resource.
            </p>
          </div>
          <div className="roster-dashboard-action" style={{ color: '#a78bfa' }}>
            <span>Manage team</span>
            <span className="arrow">→</span>
          </div>
        </Link>
      )}

      <div className="security-scanning-section">
        <h2 className="section-title">Security Scanning Status</h2>
        <SecurityScanningWidget />
      </div>

      <div className="accessibility-section">
        <h2 className="section-title">Accessibility Compliance</h2>
        <AccessibilityWidget />
      </div>

      <div className="two-column-grid">
        <div>
          <h2 className="section-title">Environment Connectors</h2>
          {connectors?.connectors ? (
            Object.entries(connectors.connectors).map(([key, conn]) => (
              <Card key={key} className="connector-card">
                <div className="connector-info">
                  <div className="connector-name">{conn.name || key}</div>
                  <div className="connector-details">
                    {conn.connected
                      ? JSON.stringify(conn.details || {})
                      : conn.error || 'Not configured'}
                  </div>
                </div>
                <Badge color={conn.connected ? '#059669' : '#dc2626'}>
                  {conn.connected ? 'Connected' : 'Disconnected'}
                </Badge>
              </Card>
            ))
          ) : (
            <>
              <Card className="connector-card">
                <div className="connector-info">
                  <div className="connector-name">New Eyvo (FastAPI/MongoDB)</div>
                  <div className="connector-details">Not configured</div>
                </div>
                <Badge color="#6b7280">Pending</Badge>
              </Card>
              <Card className="connector-card">
                <div className="connector-info">
                  <div className="connector-name">Azure Legacy Eyvo</div>
                  <div className="connector-details">Awaiting credentials</div>
                </div>
                <Badge color="#6b7280">Pending</Badge>
              </Card>
            </>
          )}

          <h2 className="section-title" style={{ marginTop: 24 }}>Open Findings</h2>
          {findings?.by_severity && Object.keys(findings.by_severity).length > 0 ? (
            <Card className="findings-summary">
              {Object.entries(findings.by_severity).map(([sev, count]) => (
                <div key={sev} className="finding-row">
                  <Badge color={STATUS_COLORS[sev] || '#6b7280'}>{sev}</Badge>
                  <span>{count} findings</span>
                </div>
              ))}
            </Card>
          ) : (
            <Card className="empty-state">
              <Activity size={32} color="#059669" />
              <p>No open findings</p>
            </Card>
          )}
        </div>

        <div>
          <h2 className="section-title">Readiness Breakdown</h2>
          {readiness?.breakdown ? (
            Object.entries(readiness.breakdown).map(([key, item]) => (
              <div key={key} className="readiness-item">
                <div className="readiness-header">
                  <span>
                    {key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                  </span>
                  <span
                    style={{
                      color:
                        item.score >= 70 ? '#059669' : item.score >= 40 ? '#d97706' : '#dc2626',
                    }}
                  >
                    {item.score}% ({item.weight}% weight)
                  </span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${item.score}%`,
                      background:
                        item.score >= 70 ? '#059669' : item.score >= 40 ? '#d97706' : '#dc2626',
                    }}
                  />
                </div>
              </div>
            ))
          ) : (
            <Card className="empty-state">
              <Activity size={32} color="#6b7280" />
              <p>Readiness data loading...</p>
            </Card>
          )}

          <h2 className="section-title" style={{ marginTop: 24 }}>Recommendations</h2>
          <Card>
            {readiness?.recommendations?.map((rec, i) => (
              <div key={i} className="recommendation">
                <span className="rec-arrow">→</span>
                {rec}
              </div>
            )) || <p className="empty-text">No recommendations at this time.</p>}
          </Card>
        </div>
      </div>
    </div>
  );
}
